import React from 'react';
import { cn } from '@rocco/utils/cn';
import { Separator } from '@rocco/components/separator';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@rocco/components/tabs';
import type { DirectConversation } from '../../types';
import { ChatSettingsSettings } from './ChatSettingsSettings';
import { ChatSettingsMembers } from './ChatSettingsMembers';

/**
 * The settings interface for the chat (direct) conversation settings.
 * Contains the conversation settings, and the participants of the conversation.
 * @see {@link ChatDetails}
 * @see {@link ChatBox}
 */
export function ChatSettings({
    className,
    conversation,
    onClose,
    onLeaveSuccess,
    onGroupChatCreated,
}: {
    className?: string;
    conversation: DirectConversation;
    /**
     * Callback when the settings are closed.
     */
    onClose?: () => void;
    /**
     * Callback when the user leaves the conversation.
     */
    onLeaveSuccess?: () => void;
    /**
     * Callback when a 1-on-1 conversation is converted to a group chat.
     */
    onGroupChatCreated?: (groupChatID: number) => void;
}) {
    return (
        <Tabs defaultValue="settings" className={cn('gap-0', className)}>
            <TabsList className="h-[36px] bg-transparent p-0">
                <ChatSettingsTabsTrigger value="settings">
                    Settings
                </ChatSettingsTabsTrigger>
                <ChatSettingsTabsTrigger value="members">
                    Members
                </ChatSettingsTabsTrigger>
            </TabsList>
            <Separator className="mb-[32px] bg-black/10" />
            <TabsContent value="settings">
                <ChatSettingsSettings
                    className="size-full"
                    conversation={conversation}
                    onClose={onClose}
                    onLeaveSuccess={onLeaveSuccess}
                />
            </TabsContent>
            <TabsContent value="members" className="flex flex-col gap-[8px]">
                <ChatSettingsMembers
                    className="size-full"
                    conversation={conversation}
                    onClose={onClose}
                    onGroupChatCreated={onGroupChatCreated}
                />
            </TabsContent>
        </Tabs>
    );
}

function ChatSettingsTabsTrigger({
    children,
    className,
    ...props
}: React.ComponentProps<typeof TabsTrigger>) {
    return (
        <TabsTrigger
            className={cn(
                'group relative rounded-none bg-transparent px-[8px] py-[4px] text-[12px] text-black/50 data-[state=active]:bg-transparent data-[state=active]:text-black',
                className,
            )}
            {...props}
        >
            {children}
            <div className="absolute -inset-x-px bottom-[-2px] hidden h-[2px] bg-black group-data-[state=active]:block"></div>
        </TabsTrigger>
    );
}
